/**
 * Single-source of the per-workload self-service capabilities (NC-984 / F2).
 *
 * Two capability dimensions had drifted apart in the UI, producing a dead end:
 * an operator could *build* an it-dev image (60–90 min, €5–10) but the finished
 * staging build could *never* be promoted to production — the "Promote to
 * Production" button simply never rendered, with no prior explanation.
 *
 * The lists below are the ONE frontend source of truth. Keep them in sync with
 * the backend, which is the actual enforcement point:
 *   - buildable  → build-images.yml matrix + `POST /api/images/build` validation
 *   - promotable → `web-portal/api/images-promote/index.ts` (returns 400 for a
 *                  non-promotable workload)
 *
 * History (NC-984): `it-dev` was **buildable but staging-only** for as long as
 * it had no production gallery target. NC-779 wired
 * `config/config.json#images.it-dev` (gallery `sig_np_shared_itdev_gwc`) and the
 * first version was promoted to production, so it-dev is now promotable — and
 * provisionable — like cam/bim. No current workload is staging-only; the rule
 * (`isStagingOnly`) and the up-front notices it drives (WorkloadEditor +
 * BuildImageDialog) stay in place for the next workload that lands in that state.
 */

// Workloads whose images can be built via the self-service pipeline.
// NC-1093 (Feat 2.0): aec (Revit+ReCap) is buildable; aec-gpu is deliberately
// NOT listed — it is a run-only twin provisioned from the aec image, so the
// editor shows no build button for it (#1090 E5).
export const BUILDABLE_WORKLOAD_IDS = ['cam', 'bim', 'it-dev', 'test', 'aec'] as const;

// Workloads whose completed staging images can be promoted to production.
// Subset of BUILDABLE_WORKLOAD_IDS (currently equal to it — NC-779 added it-dev).
export const PROMOTABLE_WORKLOAD_IDS: ReadonlySet<string> = new Set(['cam', 'bim', 'test', 'aec', 'it-dev']);

/** True when an image build can be dispatched for this workload. */
export function isBuildable(workloadId?: string | null): boolean {
  return !!workloadId && (BUILDABLE_WORKLOAD_IDS as readonly string[]).includes(workloadId);
}

/** True when a completed staging build for this workload can be promoted. */
export function isPromotable(workloadId?: string | null): boolean {
  return !!workloadId && PROMOTABLE_WORKLOAD_IDS.has(workloadId);
}

/**
 * True when a workload can be built but its images will remain in staging
 * forever (buildable ∧ ¬promotable). Drives the up-front staging-only notices.
 */
export function isStagingOnly(workloadId?: string | null): boolean {
  return isBuildable(workloadId) && !isPromotable(workloadId);
}

/**
 * NC-1288 (Soll 5): workloads that exist for testing the platform rather than for
 * running real training. Derived from the id instead of a new WorkloadDefinition
 * field: `test` is the mini-B2s probe workload from NC-216 and the only such
 * workload today, so a field would have to be back-filled on every existing doc
 * to say what the id already says. If experiment workloads become common, this
 * is the single place to swap the derivation for a persisted flag.
 */
export const TEST_WORKLOAD_IDS: ReadonlySet<string> = new Set(['test']);

export function isTestWorkload(workloadId?: string | null): boolean {
  return !!workloadId && TEST_WORKLOAD_IDS.has(workloadId);
}
